import { MessageCircle, Mail, Facebook, Instagram, Globe } from "lucide-react";
import { business, waLink } from "../config/business";
import { Link } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import { LANDING_PAGES } from "../data/landingPages";

const Heading = ({ children }) => (
  <h4 className="font-mono text-xs font-semibold uppercase tracking-widest text-slate-500">{children}</h4>
);

const SOCIALS = [
  { Icon: Facebook, href: business.social.facebook, id: "footer-social-facebook", label: "Facebook" },
  { Icon: Instagram, href: business.social.instagram, id: "footer-social-instagram", label: "Instagram" },
];

const Brand = ({ tagline }) => (
  <div>
    <div className="flex items-center gap-2.5">
      <img src="/images/logo.webp" alt={`${business.name} logo`} className="h-10 w-10 rounded-xl object-cover shadow-[0_6px_16px_-6px_rgba(234,179,8,0.5)]" />
      <span className="font-heading font-bold text-lg tracking-[-0.02em]">{business.name}</span>
    </div>
    <p className="mt-5 text-sm leading-relaxed text-slate-400">{tagline}</p>
    <div className="mt-6 flex gap-3">
      {SOCIALS.map(({ Icon, href, id, label }) => (
        <a
          key={id}
          data-testid={id}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-slate-400 transition-all duration-300 hover:-translate-y-0.5 hover:border-amber-500/50 hover:bg-amber-500/10 hover:text-amber-400"
        >
          <Icon className="h-4 w-4" />
        </a>
      ))}
    </div>
  </div>
);

const ContactLinks = ({ title, waMessage }) => (
  <div>
    <Heading>{title}</Heading>
    <div className="mt-4 space-y-3">
      <a data-testid="footer-whatsapp-link" href={waLink(waMessage)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-sm text-slate-300 transition-colors hover:text-emerald-400">
        <MessageCircle className="h-4 w-4 text-emerald-400" />
        WhatsApp: {business.phoneDisplay}
      </a>
      <a data-testid="footer-email-link" href={`mailto:${business.email}`} className="flex items-center gap-2.5 text-sm text-slate-300 transition-colors hover:text-amber-500">
        <Mail className="h-4 w-4 text-amber-500" />
        {business.email}
      </a>
      <a data-testid="footer-website-link" href={business.siteUrl} className="flex items-center gap-2.5 text-sm text-slate-300 transition-colors hover:text-sky-400">
        <Globe className="h-4 w-4 text-sky-400" />
        {business.siteDisplay}
      </a>
    </div>
  </div>
);

const Areas = ({ title }) => (
  <div>
    <Heading>{title}</Heading>
    <ul className="mt-4 space-y-1.5">
      <li className="text-sm font-semibold text-amber-300">{business.mainArea}</li>
      {business.coverage.map((c) => (
        <li key={c} className="text-sm text-slate-400">{c}</li>
      ))}
    </ul>
    <ul data-testid="footer-landing-links" className="mt-5 space-y-1.5 border-t border-white/5 pt-4">
      {LANDING_PAGES.map((p) => (
        <li key={p.slug}>
          <Link to={`/${p.slug}`} className="text-xs text-slate-500 transition-colors hover:text-amber-300">{p.h1}</Link>
        </li>
      ))}
    </ul>
  </div>
);

const Legal = ({ title, legal, legal2 }) => (
  <div>
    <Heading>{title}</Heading>
    <p data-testid="footer-legal-links" className="mt-4 text-xs leading-relaxed text-slate-500">
      <span className="font-semibold text-slate-300">{business.name}</span>
      <br />
      {legal}
      <br />
      <span className="mt-2 block">{legal2}</span>
    </p>
  </div>
);

const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer data-testid="footer-section" className="relative bg-[#070E1A]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-500/40 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#0A1526] to-transparent" />
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8 py-16 sm:py-20">
        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
          <Brand tagline={t.footer.tagline} />
          <ContactLinks title={t.footer.contactTitle} waMessage={t.hero.waMessage} />
          <Areas title={t.footer.coverageTitle} />
          <Legal title={t.footer.aboutTitle} legal={t.footer.legal} legal2={t.footer.legal2} />
        </div>
        <div className="mt-14 border-t border-white/5 pt-8 text-center text-xs text-slate-600">
          © {new Date().getFullYear()} {business.name} — {t.footer.rights}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
